import { useEffect, useRef } from "react";
import { appState } from "../../../state";
import { notifyStateChange } from "../../../stateBus";
import { mediaUrl } from "../../../mediaAuth";
import { fetchPartySessionApi, syncPartyApi } from "../../../partyApi";
import type { PartySession } from "../../../types";
import { onSocketOpen, type PartySyncData } from "../../../chatSocket";

// Party Room's counterpart to togetherSync.ts: same DOM-event-driven
// outgoing sync (play/pause/seeked on the shared element, throttled by a
// periodic tick while playing) with the same echo guard, just pointed at
// the singleton <audio id="audioPlayer"> element instead of the Lightbox's
// <video>. This means native control interaction (the <audio controls>
// scrubber/play button MusicPanel.tsx renders) syncs correctly too, not
// just PartyRoot.tsx's own play/pause/skip buttons - same reasoning as
// Together Mode not caring whether a video's play/pause came from its own
// UI or the browser's native controls.
const PARTY_SYNC_THROTTLE_MS = 2000;

export function partyTrackUrl(filename: string): string {
  return mediaUrl(`/assets/music/${encodeURIComponent(filename)}`);
}

export function getPartyAudioEl(): HTMLAudioElement | null {
  // The singleton <audio id="audioPlayer"> MusicPanel.tsx always keeps
  // mounted (portaled into #musicView, which stays in the DOM - just
  // d-none when off the /music route) - same "grab the one DOM element by
  // id" pattern togetherSync.ts uses for #mediaModalVideo.
  return document.getElementById("audioPlayer") as HTMLAudioElement | null;
}

export function usePartyLiveSync(session: PartySession | null) {
  const isApplyingRemoteRef = useRef(false);
  const sessionRef = useRef(session);
  sessionRef.current = session;
  // Tracks which filename this hook last loaded into the audio element -
  // comparing against audio.src directly is unreliable since mediaUrl()
  // appends a "?t=..." auth token query string that isn't reproducible here.
  const loadedFilenameRef = useRef<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const sessionId = session?.status === "active" ? session.id : null;

  // Outgoing: play/pause/seeked on the shared audio element (from either
  // PartyRoot's own controls or the native <audio controls> UI) sync
  // immediately; a periodic tick covers ongoing playback position drift.
  useEffect(() => {
    if (!sessionId) return;
    const audio = getPartyAudioEl();
    if (!audio) return;

    function sendNow(): void {
      if (isApplyingRemoteRef.current || !audio) return;
      const current = sessionRef.current;
      if (!current || current.currentIndex < 0) return;
      syncPartyApi(sessionId!, { currentIndex: current.currentIndex, position: audio.currentTime, playing: !audio.paused });
    }
    function startInterval(): void {
      if (intervalRef.current) return;
      intervalRef.current = setInterval(sendNow, PARTY_SYNC_THROTTLE_MS);
    }
    function stopInterval(): void {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }
    function onPlay(): void {
      sendNow();
      startInterval();
    }
    function onPause(): void {
      sendNow();
      stopInterval();
    }
    function onSeeked(): void {
      sendNow();
    }

    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPause);
    audio.addEventListener("seeked", onSeeked);
    if (!audio.paused) startInterval();

    return () => {
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPause);
      audio.removeEventListener("seeked", onSeeked);
      stopInterval();
    };
  }, [sessionId]);

  // Incoming: apply a peer's sync (track change, seek, play/pause) to the
  // shared audio element.
  function applyRemoteSync(data: PartySyncData): void {
    const current = sessionRef.current;
    if (!current || current.id !== data.sessionId) return;

    isApplyingRemoteRef.current = true;

    appState.partySession = { ...current, currentIndex: data.currentIndex, position: data.position, playing: data.playing };
    notifyStateChange();

    const audio = getPartyAudioEl();
    if (!audio) {
      isApplyingRemoteRef.current = false;
      return;
    }
    const track = data.currentIndex >= 0 ? (current.queue[data.currentIndex] ?? null) : null;
    if (!track) {
      audio.pause();
      loadedFilenameRef.current = null;
      setTimeout(() => {
        isApplyingRemoteRef.current = false;
      }, 50);
      return;
    }

    const applyPlayback = () => {
      if (Math.abs(audio.currentTime - data.position) > 1.5) audio.currentTime = data.position;
      if (data.playing && audio.paused) audio.play()?.catch(() => {});
      else if (!data.playing && !audio.paused) audio.pause();
      setTimeout(() => {
        isApplyingRemoteRef.current = false;
      }, 50);
    };

    if (loadedFilenameRef.current !== track.filename) {
      loadedFilenameRef.current = track.filename;
      audio.src = partyTrackUrl(track.filename);
      audio.addEventListener("loadedmetadata", applyPlayback, { once: true });
    } else {
      applyPlayback();
    }
  }

  // party_* events are push-only over the WS - same staleness reasoning as
  // togetherSync.ts's resyncAfterReconnect. Runs on every (re)connect,
  // including the very first.
  async function resyncAfterReconnect(): Promise<void> {
    if (!appState.partySession) return;
    const fresh = await fetchPartySessionApi();

    if (!fresh) {
      appState.partySession = null;
      notifyStateChange();
      return;
    }

    appState.partySession = fresh;
    notifyStateChange();
    if (fresh.status === "active") {
      applyRemoteSync({
        sessionId: fresh.id,
        currentIndex: fresh.currentIndex,
        position: fresh.position,
        playing: fresh.playing,
        fromUserId: fresh.peerUserId,
      });
    }
  }

  useEffect(() => {
    return onSocketOpen(() => {
      resyncAfterReconnect();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { applyRemoteSync, loadedFilenameRef };
}
